import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { ArrowLeft, Plus, Edit2, Trash2, Save, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FooterManager = () => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    label: '',
    url: '',
    category: 'diensten',
    order: 0
  });
  const [message, setMessage] = useState({ type: '', text: '' });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchLinks = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/footer`, {
        withCredentials: true
      });
      setLinks(response.data);
    } catch (error) {
      
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        `${API_URL}/api/footer`,
        formData,
        { withCredentials: true }
      );
      setLinks([...links, response.data]);
      setFormData({ label: '', url: '', category: 'diensten', order: 0 });
      setShowAddForm(false);
      setMessage({ type: 'success', text: 'Link toegevoegd!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Toevoegen mislukt' });
    }
  };

  const handleUpdate = async (linkId) => {
    try {
      const link = links.find(l => l.id === linkId);
      await axios.put(
        `${API_URL}/api/footer/${linkId}`,
        {
          label: link.label,
          url: link.url,
          category: link.category,
          order: link.order
        },
        { withCredentials: true }
      );
      setEditingId(null);
      setMessage({ type: 'success', text: 'Link bijgewerkt!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Bijwerken mislukt' });
    }
  };

  const handleDelete = async (linkId) => {
    if (!window.confirm('Weet je zeker dat je deze link wilt verwijderen?')) return;

    try {
      await axios.delete(`${API_URL}/api/footer/${linkId}`, {
        withCredentials: true
      });
      setLinks(links.filter(l => l.id !== linkId));
      setMessage({ type: 'success', text: 'Link verwijderd' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Verwijderen mislukt' });
    }
  };

  const updateLink = (id, field, value) => {
    setLinks(links.map(l => l.id === id ? { ...l, [field]: value } : l));
  };

  const groupedLinks = links.reduce((acc, link) => {
    if (!acc[link.category]) acc[link.category] = [];
    acc[link.category].push(link);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
          <p className="mt-4 text-gray-600">Laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Link to="/admin/dashboard">
                <Button variant="outline" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Terug
                </Button>
              </Link>
              <h1 className="text-2xl font-bold text-gray-900">Footer Links Beheren</h1>
            </div>
            <Button
              onClick={() => setShowAddForm(!showAddForm)}
              className="bg-blue-700 hover:bg-blue-800"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nieuwe Link
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {message.text && (
          <div
            className={`mb-6 p-4 rounded-lg ${
              message.type === 'success'
                ? 'bg-green-50 border border-green-200 text-green-700'
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}
          >
            {message.text}
          </div>
        )}

        {showAddForm && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Nieuwe Footer Link Toevoegen</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Label *
                    </label>
                    <Input
                      value={formData.label}
                      onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                      placeholder="Zoho CRM"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      URL *
                    </label>
                    <Input
                      value={formData.url}
                      onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                      placeholder="/zoho-crm"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Categorie *
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      required
                    >
                      <option value="diensten">Diensten</option>
                      <option value="producten">Zoho Producten</option>
                      <option value="info">Informatie</option>
                      <option value="contact">Contact</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Volgorde
                    </label>
                    <Input
                      type="number"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                      min="0"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button type="submit" className="bg-blue-700 hover:bg-blue-800">
                    Toevoegen
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowAddForm(false)}
                  >
                    Annuleren
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        <div className="space-y-6">
          {Object.entries(groupedLinks).map(([category, categoryLinks]) => (
            <Card key={category}>
              <CardHeader>
                <CardTitle className="capitalize">
                  {category} ({categoryLinks.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {categoryLinks.map((link) => (
                    <Card key={link.id} className="border-2">
                      <CardContent className="p-4">
                        {editingId === link.id ? (
                          <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                              <Input
                                value={link.label}
                                onChange={(e) => updateLink(link.id, 'label', e.target.value)}
                                placeholder="Label"
                              />
                              <Input
                                value={link.url}
                                onChange={(e) => updateLink(link.id, 'url', e.target.value)}
                                placeholder="URL"
                              />
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleUpdate(link.id)}
                              >
                                <Save className="w-4 h-4 mr-2" />
                                Opslaan
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setEditingId(null)}
                              >
                                <X className="w-4 h-4 mr-2" />
                                Annuleren
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex justify-between items-center">
                            <div>
                              <p className="font-semibold text-gray-900">{link.label}</p>
                              <p className="text-sm text-gray-600">{link.url}</p>
                              <p className="text-xs text-gray-500 mt-1">Volgorde: {link.order}</p>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setEditingId(link.id)}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleDelete(link.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}

          {Object.keys(groupedLinks).length === 0 && (
            <Card>
              <CardContent className="p-12 text-center">
                <p className="text-gray-600 mb-4">Nog geen footer links toegevoegd</p>
                <Button onClick={() => setShowAddForm(true)}>
                  Voeg je eerste link toe
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
};

export default FooterManager;